import { Select } from '@react-apps/design-system';
import {
  scopeOptionLabel,
  useScopeFormatter,
  useScopes,
} from '@/shared/scopes/use-scopes';
import { TagChips } from './TagChips';
import { TagListInput } from './TagListInput';

interface ScopeListInputProps {
  label: string;
  values: string[];
  helperText?: string;
  onChange: (values: string[]) => void;
}

/**
 * Escolha de scopes a partir da lista do back (`GET /admin/identity/scopes`),
 * no lugar de digitar UUID à mão. Os já escolhidos saem das opções e viram
 * chips removíveis.
 *
 * Se a lista não puder ser carregada, cai para {@link TagListInput} com entrada
 * livre: a rota de scopes exige papel do Identity, e quem só tem papel de
 * Catálogo ficaria impedido de criar perk/role/feature se o campo dependesse
 * dela.
 */
export function ScopeListInput({
  label,
  values,
  helperText,
  onChange,
}: ScopeListInputProps) {
  const { data: scopes, isLoading, isError } = useScopes();
  const { scopeLabel } = useScopeFormatter();

  // O chip mostra só o nome — quem escolheu de uma lista nomeada não precisa
  // reler o UUID. Sem nome conhecido, o id é o que resta.
  const chipLabel = (id: string) => scopeLabel(id) ?? id;

  if (isError || (!isLoading && !scopes?.length)) {
    return (
      <TagListInput
        label={label}
        values={values}
        placeholder="scopeId…"
        helperText={`${helperText ? `${helperText} ` : ''}Lista de scopes indisponível — informe o id manualmente.`}
        onChange={onChange}
        formatValue={chipLabel}
      />
    );
  }

  const available = (scopes ?? []).filter((s) => !values.includes(s.id));

  return (
    <div className="space-y-2">
      <Select
        label={label}
        // Volta sempre para o placeholder: o select é um "adicionar", não a
        // representação do valor atual — quem representa é a lista de chips.
        value=""
        disabled={isLoading}
        onChange={(e) => {
          if (e.target.value) onChange([...values, e.target.value]);
        }}
        placeholder={
          isLoading
            ? 'Carregando scopes…'
            : available.length
              ? 'Adicionar scope…'
              : 'Todos os scopes já adicionados'
        }
        options={available.map((s) => ({
          value: s.id,
          label: scopeOptionLabel(s),
        }))}
        helperText={helperText}
      />
      <TagChips
        values={values}
        onRemove={(value) => onChange(values.filter((v) => v !== value))}
        formatValue={chipLabel}
      />
    </div>
  );
}
